import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Stack, TextField, ToggleButtonGroup, ToggleButton, IconButton, Slider,
} from '@mui/material';
import {
  collection, query, where, onSnapshot, getDocs, Timestamp, doc, updateDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { Table, Reservation } from '../types';

const CELL = 44;
const DEFAULT_TURN = 120; // 滞在の既定(分)

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function nowMinNow(): number { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); }
function toMin(hm?: string | null): number | null {
  if (!hm) return null;
  const [h, m] = hm.slice(0, 5).split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}
function hm(min: number): string { return `${String(Math.floor((min % 1440) / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`; }
function durMin(r: Reservation): number {
  const s = toMin(r.startTime); const e = toMin(r.endTime);
  if (s != null && e != null && e > s) return e - s;
  if (r.duration && r.duration > 0) return r.duration;
  return DEFAULT_TURN;
}
function endMin(r: Reservation): number { const s = toMin(r.startTime) ?? 0; return s + durMin(r); }
function coversAt(r: Reservation, minute: number): boolean { const s = toMin(r.startTime); return s != null && s <= minute && minute < s + durMin(r); }
function capOf(t: Table): number { return t.totalSeats ?? t.maxCapacity ?? t.seats ?? 1; }

function withAutoLayout(tables: Table[]): Table[] {
  let i = 0; const COLS = 6;
  return tables.map((t) => {
    if (t.posX != null && t.posY != null) return t;
    const w = t.width ?? (t.seatType === 'counter' ? Math.max(3, Math.min(9, capOf(t))) : 2), h = t.height ?? 2;
    const col = i % COLS, row = Math.floor(i / COLS); i++;
    return { ...t, posX: col * 4, posY: row * 3, width: w, height: h };
  });
}

export default function FloorMap() {
  const { shop } = useAuth();
  const [tables, setTables] = useState<Table[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [date, setDate] = useState<string>(todayStr());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, Partial<Table>>>({});
  const [saving, setSaving] = useState(false);
  const [focusMin, setFocusMin] = useState<number | null>(null); // null=全日

  // 空き検索
  const [avOpen, setAvOpen] = useState(false);
  const [avParty, setAvParty] = useState(2);
  const [avTime, setAvTime] = useState('19:00');
  const [avDays, setAvDays] = useState(14);
  const [avResult, setAvResult] = useState<{ date: string; weekday: string; canSeat: boolean; freeTables: number; freeCounterSeats: number }[] | null>(null);
  const [avLoading, setAvLoading] = useState(false);

  useEffect(() => {
    if (!shop) return;
    const q = query(collection(db, 'tables'), where('shopId', '==', shop.id));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Table[];
      data.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));
      setTables(data.filter((t) => t.isActive !== false));
    });
  }, [shop]);

  useEffect(() => {
    if (!shop || !date) return;
    const [y, m, d] = date.split('-').map(Number);
    const start = new Date(y, m - 1, d); const end = new Date(y, m - 1, d + 1);
    const q = query(collection(db, 'reservations'), where('shopId', '==', shop.id),
      where('reservationDate', '>=', Timestamp.fromDate(start)), where('reservationDate', '<', Timestamp.fromDate(end)));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data(), reservationDate: doc.data().reservationDate?.toDate() })) as Reservation[];
      setReservations(data.filter((r) => ['confirmed', 'pending'].includes(r.status)));
    });
  }, [shop, date]);

  const isToday = date === todayStr();
  const checkMin = focusMin != null ? focusMin : (isToday ? nowMinNow() : null);

  const byTable = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    for (const r of reservations) { if (!r.tableId) continue; const a = map.get(r.tableId) ?? []; a.push(r); map.set(r.tableId, a); }
    map.forEach((a) => a.sort((x, y) => (x.startTime ?? '').localeCompare(y.startTime ?? '')));
    return map;
  }, [reservations]);

  const laidOut = useMemo(() => withAutoLayout(tables.map((t) => ({ ...t, ...draft[t.id] }))), [tables, draft]);
  const unassigned = reservations.filter((r) => !r.tableId);
  const maxX = laidOut.reduce((m, t) => Math.max(m, (t.posX ?? 0) + (t.width ?? 2)), 8) + 1;
  const maxY = laidOut.reduce((m, t) => Math.max(m, (t.posY ?? 0) + (t.height ?? 2)), 6) + 1;

  const nudge = (dx: number, dy: number) => { if (!selectedId) return; setDraft((p) => { const c = laidOut.find((t) => t.id === selectedId)!; return { ...p, [selectedId]: { ...p[selectedId], posX: Math.max(0, (c.posX ?? 0) + dx), posY: Math.max(0, (c.posY ?? 0) + dy) } }; }); };
  const resize = (dw: number, dh: number) => { if (!selectedId) return; setDraft((p) => { const c = laidOut.find((t) => t.id === selectedId)!; return { ...p, [selectedId]: { ...p[selectedId], width: Math.max(1, (c.width ?? 2) + dw), height: Math.max(1, (c.height ?? 2) + dh) } }; }); };

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await Promise.all(laidOut.map((t) => updateDoc(doc(db, 'tables', t.id), {
        posX: t.posX ?? 0, posY: t.posY ?? 0, width: t.width ?? 2, height: t.height ?? 2, shape: t.shape ?? 'rect', updatedAt: Timestamp.now(),
      })));
      setDraft({}); setMode('view');
    } finally { setSaving(false); }
  }, [laidOut]);

  // 複数日 空き検索（指定時刻に在席する予約を除いた空き卓/カウンター席）
  const runAvailability = useCallback(async () => {
    if (!shop) return;
    setAvLoading(true);
    try {
      const tMin = toMin(avTime) ?? 1140;
      const [y, m, d] = todayStr().split('-').map(Number);
      const start = new Date(y, m - 1, d); const end = new Date(y, m - 1, d + avDays);
      const snap = await getDocs(query(collection(db, 'reservations'), where('shopId', '==', shop.id),
        where('reservationDate', '>=', Timestamp.fromDate(start)), where('reservationDate', '<', Timestamp.fromDate(end))));
      const resv = snap.docs.map((doc) => ({ id: doc.id, ...doc.data(), reservationDate: doc.data().reservationDate?.toDate() })) as Reservation[];
      const active = resv.filter((r) => ['confirmed', 'pending'].includes(r.status));
      const tableUnits = tables.filter((t) => t.seatType !== 'counter');
      const counterSeats = tables.filter((t) => t.seatType === 'counter').reduce((s, t) => s + capOf(t), 0);
      const wd = ['日', '月', '火', '水', '木', '金', '土'];
      const out: { date: string; weekday: string; canSeat: boolean; freeTables: number; freeCounterSeats: number }[] = [];
      for (let i = 0; i < avDays; i++) {
        const dd = new Date(y, m - 1, d + i);
        const ds = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`;
        const dayResv = active.filter((r) => {
          const rd = r.reservationDate as Date; return rd && rd.getFullYear() === dd.getFullYear() && rd.getMonth() === dd.getMonth() && rd.getDate() === dd.getDate() && coversAt(r, tMin);
        });
        const occupiedTableIds = new Set(dayResv.filter((r) => r.tableId).map((r) => r.tableId));
        const freeTables = tableUnits.filter((t) => !occupiedTableIds.has(t.id) && capOf(t) >= avParty).length;
        const counterUsed = dayResv.filter((r) => { const t = tables.find((x) => x.id === r.tableId); return t?.seatType === 'counter'; }).reduce((s, r) => s + (r.numberOfPeople ?? 1), 0);
        const freeCounterSeats = Math.max(0, counterSeats - counterUsed);
        out.push({ date: ds, weekday: wd[dd.getDay()], canSeat: freeTables > 0 || freeCounterSeats >= avParty, freeTables, freeCounterSeats });
      }
      setAvResult(out);
    } finally { setAvLoading(false); }
  }, [shop, tables, avTime, avDays, avParty]);

  if (!shop) return <Box p={3}><Typography color="text.secondary">店舗を選択してください</Typography></Box>;
  const selected = laidOut.find((t) => t.id === selectedId) ?? null;

  // カウンター: 席ごとの占有を人数で詰める
  function counterSeatOcc(t: Table): (Reservation | null)[] {
    const cap = Math.max(1, capOf(t));
    const occ: (Reservation | null)[] = Array.from({ length: cap }, () => null);
    const resv = (byTable.get(t.id) ?? []).filter((r) => checkMin == null ? true : coversAt(r, checkMin));
    let idx = 0;
    for (const r of resv) { const need = Math.max(1, r.numberOfPeople ?? 1); for (let k = 0; k < need && idx < cap; k++) occ[idx++] = r; }
    return occ;
  }

  return (
    <Box p={{ xs: 1.5, sm: 3 }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Typography variant="h5" fontWeight={700}>🪑 空席表（フロアMAP）</Typography>
        <TextField type="date" size="small" value={date} onChange={(e) => { setDate(e.target.value); setFocusMin(null); }} />
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="view">閲覧</ToggleButton>
          <ToggleButton value="edit">レイアウト編集</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" variant={avOpen ? 'contained' : 'outlined'} onClick={() => setAvOpen((v) => !v)}>🔎 空き検索</Button>
      </Stack>

      {/* 空き検索 */}
      {avOpen && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
            <TextField type="number" size="small" label="人数" sx={{ width: 90 }} value={avParty} onChange={(e) => setAvParty(Math.max(1, Number(e.target.value)))} />
            <TextField type="time" size="small" label="時刻" value={avTime} onChange={(e) => setAvTime(e.target.value)} />
            <TextField type="number" size="small" label="先何日" sx={{ width: 100 }} value={avDays} onChange={(e) => setAvDays(Math.min(62, Math.max(1, Number(e.target.value))))} />
            <Button variant="contained" disabled={avLoading} onClick={runAvailability}>{avLoading ? '検索中…' : '空きを検索'}</Button>
          </Stack>
          {avResult && (
            <Stack direction="row" gap={1} flexWrap="wrap" mt={1.5}>
              {avResult.map((r) => (
                <Box key={r.date} onClick={() => r.canSeat && setDate(r.date)}
                  sx={{ px: 1, py: 0.75, borderRadius: 1, border: '1px solid', cursor: r.canSeat ? 'pointer' : 'default',
                    borderColor: r.canSeat ? 'success.main' : 'error.light', bgcolor: r.canSeat ? 'success.50' : 'error.50', minWidth: 64, textAlign: 'center', opacity: r.canSeat ? 1 : 0.6 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{r.date.slice(5)}({r.weekday})</Typography>
                  <Typography sx={{ fontSize: 10 }}>{r.canSeat ? `卓${r.freeTables}/席${r.freeCounterSeats}` : '満'}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {/* 時間帯スライダー */}
      <Stack direction="row" gap={2} alignItems="center" mb={1.5} flexWrap="wrap">
        <Typography variant="caption">時間帯</Typography>
        <Button size="small" variant={focusMin == null ? 'contained' : 'outlined'} onClick={() => setFocusMin(null)}>全日</Button>
        {isToday && <Button size="small" variant={focusMin === nowMinNow() ? 'contained' : 'outlined'} onClick={() => setFocusMin(nowMinNow())}>現在</Button>}
        <Slider sx={{ maxWidth: 360 }} min={600} max={1425} step={15} value={focusMin ?? 1140} onChange={(_, v) => setFocusMin(v as number)} />
        <Typography variant="body2" fontWeight={700} color={focusMin != null ? 'primary.main' : 'text.secondary'} sx={{ fontFamily: 'monospace', width: 56 }}>{focusMin != null ? hm(focusMin) : '全日'}</Typography>
        {checkMin != null && <Typography variant="caption" color="warning.main">橙=空きだが{DEFAULT_TURN}分内に予約</Typography>}
      </Stack>

      {mode === 'edit' && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box><Typography variant="caption" display="block" textAlign="center">移動</Typography>
            <Stack alignItems="center"><IconButton size="small" onClick={() => nudge(0, -1)}>↑</IconButton>
              <Stack direction="row"><IconButton size="small" onClick={() => nudge(-1, 0)}>←</IconButton><IconButton size="small" onClick={() => nudge(0, 1)}>↓</IconButton><IconButton size="small" onClick={() => nudge(1, 0)}>→</IconButton></Stack></Stack></Box>
          <Box><Typography variant="caption" display="block" textAlign="center">サイズ</Typography>
            <Stack direction="row" gap={0.5} alignItems="center"><Typography variant="caption">幅</Typography>
              <Button size="small" variant="outlined" onClick={() => resize(-1, 0)}>−</Button><Button size="small" variant="outlined" onClick={() => resize(1, 0)}>＋</Button>
              <Typography variant="caption">高</Typography><Button size="small" variant="outlined" onClick={() => resize(0, -1)}>−</Button><Button size="small" variant="outlined" onClick={() => resize(0, 1)}>＋</Button></Stack></Box>
          <Typography variant="caption" color="text.secondary">{selected ? `選択中: ${selected.tableName || selected.tableNumber}` : 'テーブルをクリックして選択'}</Typography>
          <Button sx={{ ml: 'auto' }} variant="contained" disabled={saving} onClick={save}>{saving ? '保存中…' : 'レイアウトを保存'}</Button>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ overflow: 'auto', p: 1, maxHeight: '72vh' }}>
        <Box sx={{ position: 'relative', width: maxX * CELL, height: maxY * CELL,
          backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.06) 1px, transparent 1px)', backgroundSize: `${CELL}px ${CELL}px` }}
          onClick={(e) => { if (e.currentTarget === e.target) setSelectedId(null); }}>
          {laidOut.map((t) => {
            const isSel = selectedId === t.id;
            const isCounter = t.seatType === 'counter' && capOf(t) > 1;
            const isRoom = t.seatType === 'privateRoom' || t.seatType === 'semiPrivate';
            const baseSx = {
              position: 'absolute' as const, left: (t.posX ?? 0) * CELL, top: (t.posY ?? 0) * CELL,
              width: (t.width ?? 2) * CELL, height: (t.height ?? 2) * CELL,
              border: isSel ? '2px solid #1976d2' : isRoom ? '2px dashed #9c27b0' : '1px solid rgba(0,0,0,0.2)',
              borderRadius: t.shape === 'round' ? '50%' : 1, boxSizing: 'border-box' as const, cursor: 'pointer', overflow: 'hidden',
            };
            if (isCounter) {
              const occ = counterSeatOcc(t);
              return (
                <Box key={t.id} sx={{ ...baseSx, display: 'flex' }} onClick={(e) => { e.stopPropagation(); setSelectedId(t.id); }}>
                  {occ.map((r, i) => (
                    <Box key={i} title={r ? `${r.startTime} ${r.numberOfPeople ?? ''}名 ${r.userName ?? ''}` : `${t.tableName || t.tableNumber}${i + 1}`}
                      sx={{ flex: 1, minWidth: 0, borderRight: i < occ.length - 1 ? '1px solid rgba(0,0,0,0.15)' : 'none',
                        bgcolor: r ? 'rgba(74,144,226,0.85)' : 'rgba(0,0,0,0.03)', color: r ? '#fff' : 'text.secondary',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
                      {r ? (r.startTime ?? '').slice(0, 5) : `${(t.tableName || t.tableNumber || 'C').slice(0, 1)}${i + 1}`}
                    </Box>
                  ))}
                </Box>
              );
            }
            const resv = byTable.get(t.id) ?? [];
            const coveringNow = checkMin != null ? resv.find((r) => coversAt(r, checkMin)) : null;
            const occupied = t.status === 'occupied' || !!coveringNow || (resv.length > 0 && checkMin == null);
            const headline = coveringNow ?? resv[0];
            // ウォークイン時間ガード（全日でなく現在/指定時刻基準・空きだが間もなく予約）
            const upcoming = checkMin != null && !coveringNow ? resv.find((r) => { const s = toMin(r.startTime); return s != null && s > checkMin && s - checkMin <= DEFAULT_TURN; }) : null;
            return (
              <Box key={t.id} onClick={(e) => { e.stopPropagation(); setSelectedId(t.id); }}
                sx={{ ...baseSx, border: upcoming ? '2px solid #f59e0b' : baseSx.border,
                  bgcolor: occupied ? 'rgba(74,144,226,0.85)' : upcoming ? 'rgba(245,158,11,0.18)' : 'rgba(0,0,0,0.03)',
                  color: occupied ? '#fff' : 'text.primary', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.1 }}>{isRoom ? '🚪' : ''}{t.tableName || t.tableNumber}</Typography>
                {headline ? (
                  <>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1.1 }}>{(headline.startTime ?? '').slice(0, 5)}〜{hm(endMin(headline))}</Typography>
                    <Typography sx={{ fontSize: 9, lineHeight: 1.1 }}>{(headline.numberOfPeople ?? '')}名 {headline.userName ?? ''}</Typography>
                  </>
                ) : upcoming ? (
                  <Typography sx={{ fontSize: 9, color: '#b45309', fontWeight: 700 }}>⏰{(upcoming.startTime ?? '').slice(0, 5)}予約</Typography>
                ) : (
                  <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>×{t.maxCapacity ?? t.seats ?? ''}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Box mt={2}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>予約一覧（{reservations.length}件 / 未割当 {unassigned.length}）</Typography>
        <Stack gap={0.5} sx={{ maxHeight: 300, overflow: 'auto' }}>
          {[...reservations].sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? '')).map((r) => {
            const t = tables.find((x) => x.id === r.tableId);
            return (
              <Stack key={r.id} direction="row" gap={2} alignItems="center" sx={{ borderLeft: '3px solid', borderColor: r.tableId ? 'primary.main' : 'warning.main', pl: 1, py: 0.25, fontSize: 14 }}>
                <Box sx={{ fontFamily: 'monospace', width: 96 }}>{r.startTime}〜{r.endTime}</Box>
                <Box sx={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.userName ?? '(無名)'}</Box>
                <Box sx={{ color: 'text.secondary' }}>{r.numberOfPeople ?? ''}名</Box>
                <Box sx={{ fontSize: 12, px: 1, borderRadius: 1, bgcolor: r.tableId ? 'transparent' : 'warning.light' }}>{t ? (t.tableName || t.tableNumber) : '未割当'}</Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
